import React from 'react';

import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  YAxis,
  XAxis,
  CartesianGrid,
  Tooltip,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  COLOR_GREY_400,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import { abbreviateNumber, convertCurrency } from 'src/utils/functions';
import { exchangeRates } from 'src/utils/const';
import { wrapLabel } from '../Text/utils';
import DataChartTooltip from 'src/App/Dashboards/Market/Components/Tooltips/DataChartTooltip';

interface ReferenceBarChartProps {
  xAxisDataKey: string;
  yAxisLabel: string;
  yAxisRange: [number, number];
  data: any;
  yAxisDataKey: string;
  referenceText: string;
  referenceValue: number;
  unit: string;
  isUserAllowed: boolean;
  isMobileView: boolean;
  CustomTooltipComponent?: React.ComponentType<any>;
  tooltipPropKeys?: string[];
  currency?: string;
}

const ReferenceBarChart = ({
  xAxisDataKey,
  yAxisDataKey,
  yAxisLabel,
  data,
  referenceText,
  referenceValue,
  CustomTooltipComponent,
  tooltipPropKeys = [],
  unit,
  isUserAllowed,
  isMobileView,
  currency,
  yAxisRange,
}: ReferenceBarChartProps) => {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as any;
    if (!isUserAllowed && !data.isReference) return null;

    return CustomTooltipComponent ? (
      <CustomTooltipComponent
        {...tooltipPropKeys.reduce((acc: any, key: string) => {
          acc[key] = data[key];
          return acc;
        }, {})}
      />
    ) : (
      <DataChartTooltip
        data={[
          {
            name: data[xAxisDataKey],
            dataKey: yAxisLabel,
            val:
              unit == '$' && currency
                ? abbreviateNumber(convertCurrency(currency, data[yAxisDataKey]), false)
                : abbreviateNumber(data[yAxisDataKey], false) + (unit == '%' ? '%' : ''),
            color: data.isReference ? COLOR_PRIMARY_1 : COLOR_PRIMARY_2,
          },
        ]}
      />
    );
  };

  return (
    <Box sx={{ height: 300, mb: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={COLOR_GREY_400} vertical={false} />

          <YAxis
            domain={yAxisRange}
            tickLine={false}
            axisLine={{
              stroke: COLOR_TEXT_2,
              strokeDasharray: '4 4',
            }}
            tick={props => {
              const { x, y, payload } = props;
              const value =
                unit == '$' && currency
                  ? abbreviateNumber(convertCurrency(currency, payload.value), false)
                  : abbreviateNumber(payload.value, false);

              return (
                <text
                  x={x}
                  y={y}
                  dy={4}
                  fontSize={12}
                  fontWeight={500}
                  fill={COLOR_TEXT_2}
                  textAnchor="end"
                >
                  {unit == '$' && currency && exchangeRates[currency].symbol}
                  <tspan style={{ filter: !isUserAllowed ? 'blur(5px)' : 'none' }}>{value}</tspan>
                  {unit == '%' && <tspan>%</tspan>}
                </text>
              );
            }}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: {
                textAnchor: 'middle',
                fontSize: 12,
                fontWeight: 600,
                fill: COLOR_TEXT_2,
              },
            }}
          />

          <XAxis
            dataKey={xAxisDataKey}
            axisLine={false}
            tickLine={false}
            height={80}
            interval={0}
            tick={props => {
              const { x, y, payload } = props;
              const lines = wrapLabel(String(payload.value), 12);
              const dataPoint = data[payload.index];

              return (
                <text
                  x={x}
                  y={y + 10}
                  textAnchor="end"
                  fontSize={12}
                  fontWeight={500}
                  fill={COLOR_TEXT_2}
                  transform={`rotate(-45, ${x}, ${y + 10})`}
                >
                  {lines.map((line, i) => (
                    <tspan
                      key={i}
                      x={x}
                      dy={i === 0 ? 0 : 14}
                      style={{
                        filter: !isUserAllowed && !dataPoint.isReference ? 'blur(5px)' : 'none',
                      }}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            }}
          />

          {/* Next to sub market -> add (add submarkt text) similar for market */}

          <Tooltip
            content={<CustomTooltip />}
            cursor={false}
            interactive={true}
            wrapperStyle={{ pointerEvents: 'auto' }}
          />

          <Bar
            dataKey={yAxisDataKey}
            radius={[4, 4, 0, 0]}
            barSize={isMobileView ? 20 : 40}
            animationDuration={1000}
            animationBegin={0}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isReference ? COLOR_PRIMARY_1 : COLOR_PRIMARY_2}
              />
            ))}
          </Bar>
          <ReferenceLine
            y={referenceValue}
            stroke={COLOR_SECONDARY_1}
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: referenceText,
              position: 'top',
              fill: COLOR_SECONDARY_1,
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ReferenceBarChart;
